import { Link } from "@tanstack/react-router";
import { MeetChrisVideoButton } from "@/components/MeetChrisVideoButton";
import { useEffect, useState, type ReactNode } from "react";
import {
  Globe,
  Search,
  MapPin,
  Target,
  Megaphone,
  Phone,
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  PhoneCall,
  Calendar,
  Star,
  Sparkles,
  ShieldCheck,
  Zap,
  MessageSquare,
  XCircle,
  Heart,
  Award,
  Users,
  Clock,
  Handshake,
  DollarSign,
  Bot,
  PlayCircle,
  GraduationCap,
  Wrench,
  Hammer,
  Home as HomeIcon,
  Trees,
  Truck,
  HardHat,
  Sparkle,
  Eye,
  Scale,
  Stethoscope,
  Utensils,
  Car,
  GlassWater,
  DoorOpen,
} from "lucide-react";
import heroImg from "@/assets/hero-colorado.jpg";
import heroVideoUrl from "@/assets/hero-colorado.mp4?url";
import { ProposalCapture } from "@/components/ProposalCapture";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import portfolio1 from "@/assets/portfolio-1.jpg";
import portfolio2 from "@/assets/portfolio-2.jpg";
import portfolio3 from "@/assets/portfolio-3.jpg";
import ownerImg from "@/assets/chris-heidlebaugh-web-design-agency-owner-colorado.png";
import mapsProof1 from "@/assets/maps-proof-1.jpg";
import mapsProof2 from "@/assets/maps-proof-2.jpg";
import quickServicesImg from "@/assets/quick-services.jpg";
import quickPricingImg from "@/assets/quick-pricing.jpg";
import quickIndustriesImg from "@/assets/quick-industries.jpg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/* ----------------------------- HERO ----------------------------- */
export function Hero() {
  // Mount the background video right after hydration on all devices. The
  // static hero image (LCP element) still paints first via the preload hint
  // in the route head; the video then layers on top once it can autoplay.
  const [mountVideo, setMountVideo] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setMountVideo(true);
  }, []);


  return (
    <section className="relative isolate overflow-hidden">
      {/* Hero image — the actual LCP element. Paints immediately from the
          preload hint in src/routes/index.tsx. The video lazy-mounts after
          this image loads (or after idle), keeping initial page load lean. */}
      <img
        src={heroImg}
        alt="Colorado Rocky Mountains at golden hour"
        width={1920}
        height={1280}
        decoding="async"
        fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {mountVideo && (
        <video
          src={heroVideoUrl}
          poster={heroImg}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          ref={(el) => {
            // Some mobile browsers reject autoplay without an explicit play() call.
            if (el) {
              const p = el.play();
              if (p && typeof p.catch === "function") p.catch(() => { /* ignore */ });
            }
          }}
          className="absolute inset-0 h-full w-full object-cover animate-fade-in"
        />
      )}
      <div className="absolute inset-0 bg-[image:var(--gradient-hero-overlay)]" />
      <div className="absolute inset-0 bg-gradient-radial-gold opacity-50" />

      <div className="relative mx-auto flex min-h-[88vh] w-full max-w-7xl flex-col items-start justify-center px-4 py-20 text-brand-cream sm:px-5 sm:py-28 lg:px-8">
        <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1.5 text-[10px] font-semibold uppercase leading-tight tracking-[0.14em] text-brand-gold backdrop-blur animate-fade-up sm:px-4 sm:text-xs sm:tracking-[0.18em]">
          <Sparkles className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
          <span className="truncate sm:whitespace-normal">Colorado Springs Digital Marketing Agency</span>
        </div>

        <h1
          className="mt-6 w-full max-w-4xl text-[2rem] font-black leading-[1.05] text-white [overflow-wrap:anywhere] sm:text-6xl lg:text-7xl animate-fade-up"
          style={{ animationDelay: "60ms" }}
        >
          <span className="sr-only">Colorado Springs Digital Marketing Agency — </span>
          <span className="block text-balance">Get More</span>
          <span className="block text-balance text-gradient-gold">
            Calls, Leads &amp; Jobs
          </span>
          <span className="block text-balance">From Your Website</span>
          <span className="block text-balance">&amp; Marketing</span>
        </h1>


        <p
          className="mt-6 max-w-2xl text-base leading-relaxed text-brand-cream/85 sm:text-xl animate-fade-up"
          style={{ animationDelay: "140ms" }}
        >
          We help Colorado businesses get more calls, rank higher on Google, and turn their website
          into a consistent lead source — without pushy sales teams or outsourced work.
        </p>

        <div
          className="mt-5 flex items-start gap-3 text-sm font-semibold text-brand-cream animate-fade-up"
          style={{ animationDelay: "180ms" }}
        >
          <Heart className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
          <span className="text-balance">Talk To The Owner Directly—not a salesperson, account manager, or outsourced team.</span>
        </div>

        <div
          className="mt-8 flex w-full max-w-2xl flex-col items-stretch gap-4 animate-fade-up sm:items-start"
          style={{ animationDelay: "220ms" }}
        >
          {/* 3-Level CTA system: matches buyer intent (high → mid → low) */}
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
            {/* PRIMARY — High intent: ready to talk, wants a tangible next step */}
            <Link
              to="/contact"
              className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-md bg-gradient-gold px-6 text-sm font-bold uppercase tracking-wider text-brand-black shadow-gold transition-spring hover:-translate-y-0.5 sm:w-auto sm:px-8"
            >
              Get a Free Proposal
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>

            {/* SECONDARY — Mid intent: exploring what we do */}
            <Link
              to="/digital-marketing"
              className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-md border border-brand-cream/30 bg-white/5 px-6 text-sm font-bold uppercase tracking-wider text-brand-cream backdrop-blur transition-colors hover:border-brand-gold hover:bg-white/10 hover:text-white sm:w-auto"
            >
              Our Services
            </Link>

            {/* TERTIARY — Low intent: price shoppers / research mode */}
            <Link
              to="/pricing"
              className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-md border border-brand-gold/40 bg-brand-gold/5 px-6 text-center text-sm font-bold uppercase tracking-wider text-brand-gold backdrop-blur transition-spring hover:-translate-y-0.5 hover:border-brand-gold hover:bg-brand-gold/15 hover:text-white sm:w-auto"
            >
              View Pricing
            </Link>
          </div>

          {/* Friction-removal microcopy */}
          <p className="text-sm font-medium text-brand-cream/80">
            Not sure what you need? <span className="text-brand-cream">We'll figure it out for you.</span>
          </p>

          {/* Star-rating proof line — placed adjacent to CTA to lift conversion */}
          <p className="text-xs font-semibold text-brand-cream/85">
            <span className="text-brand-gold">★★★★★</span> 150+ 5-Star Reviews across Colorado
          </p>

          {/* "No website yet" objection-handler */}
          <p className="text-xs font-medium text-brand-cream/85">
            No website yet? No problem — we'll build a plan from scratch.
          </p>

          {/* Phone option for talkers */}
          <a
            href="tel:+17194193935"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-cream/85 hover:text-brand-cream"
           aria-label="Call (719) 419-3935">
            <Phone className="h-3.5 w-3.5" />
            Or call (719) 419-3935
          </a>
        </div>

        <div
          className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-brand-cream/80 animate-fade-up"
          style={{ animationDelay: "300ms" }}
        >
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand-gold" />
            <span>Colorado-Only Agency</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-brand-gold" />
            <span>25+ Years of Experience</span>
          </div>
        </div>
      </div>
    </section>
  );
}


/* --------------------------- INTENT SELECTOR --------------------------- */
export function IntentSelector() {
  const options = [
    {
      intent: "leads",
      title: "I Need More Leads",
      subtext: "SEO, Google Maps, and ads that drive calls",
      icon: TrendingUp,
      to: "/colorado-springs-seo",
    },
    {
      intent: "website",
      title: "I Need a New Website",
      subtext: "Fast, modern sites that actually convert",
      icon: Globe,
      to: "/colorado-springs-web-design",
    },
    {
      intent: "both",
      title: "I Need Marketing",
      subtext: "Full-service digital marketing to grow your brand & leads",
      icon: Sparkles,
      to: "/digital-marketing",
    },
    {
      intent: "audit",
      title: "Not Sure — Show Me What To Fix",
      subtext: "We'll audit everything and guide you",
      icon: Eye,
      to: "/free-audit",
    },
  ] as const;

  return (
    <section className="relative overflow-hidden bg-gradient-dark py-16 text-brand-cream sm:py-24">
      <div className="absolute inset-0 bg-gradient-radial-gold opacity-40" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
            Pick Your Path
          </span>
          <div className="my-8 flex justify-center sm:my-10">
            <MeetChrisVideoButton variant="gold" />
          </div>
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            What do you need help with?
          </h2>
          <p className="mt-3 text-base text-brand-cream/85">
            Tap the option that fits — we'll tailor the conversation to exactly what you need.
          </p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {options.map((o) => (
            <a
              key={o.intent}
              href={`${o.to}?intent=${o.intent}`}
              className="group relative flex flex-col rounded-2xl border border-white/10 bg-white/5 p-6 text-left shadow-lg backdrop-blur-sm transition-all hover:-translate-y-1 hover:border-brand-gold hover:bg-white/10 hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-gold/20 text-brand-gold transition-colors group-hover:bg-brand-gold group-hover:text-brand-black">
                <o.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-lg font-bold leading-snug text-white">{o.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-cream/85">
                {o.subtext}
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold">
                Get started
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- TRUST BAR (compact) --------------------------- */
export function TrustBar() {
  const badges = [
    { value: "150+", label: "5-Star Reviews", icon: Star },
    { value: "25+ Yrs", label: "Since 2006", icon: Award },
    { value: "500+", label: "Businesses Helped", icon: Users },
    { value: "Owner-Led", label: "No Salespeople", icon: Handshake },
    { value: "1 hr", label: "Avg Response", icon: Clock },
    { value: "100%", label: "Colorado Owned", icon: MapPin },
    { value: "Colorado Springs", label: "HQ", icon: MapPin },
  ];
  return (
    <section className="border-y border-border bg-background py-6 sm:py-7">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-sm">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Trusted across Colorado:
          </span>
          {badges.map((b) => (
            <div key={b.label} className="flex items-center gap-2">
              <b.icon className="h-4 w-4 text-brand-gold-deep" />
              <span className="text-sm font-extrabold text-foreground">{b.value}</span>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {b.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- QUICK ANSWERS --------------------------- */
export function QuickAnswers() {
  const [videoOpen, setVideoOpen] = useState(false);
  const cards = [
    {
      img: quickServicesImg,
      alt: "Designer building a website on a laptop",
      eyebrow: "What We Do",
      title: "Services",
      desc: "Web design, SEO, Google Maps, PPC, social & AI search — built to generate leads.",
      href: "/digital-marketing",
      cta: "See services",
      accent: "from-slate-400 via-slate-500 to-slate-600",
      ctaClass: "text-slate-500",
    },
    {
      img: quickPricingImg,
      alt: "Calculator and money on a desk representing transparent pricing",
      eyebrow: "What It Costs",
      title: "Pricing",
      desc: "Honest, transparent packages. No long contracts, no hidden fees, no surprises.",
      href: "/pricing",
      cta: "View pricing",
      accent: "from-brand-gold via-brand-gold-bright to-brand-gold-deep",
      ctaClass: "text-brand-gold-deep",
    },
    {
      img: quickIndustriesImg,
      alt: "Friendly Colorado home services contractor smiling at the camera",
      eyebrow: "Who It's For",
      title: "Industries",
      desc: "Home services, contractors, trades & local Colorado businesses we specialize in.",
      href: "/industries",
      cta: "See industries",
      accent: "from-zinc-700 via-zinc-800 to-zinc-900",
      ctaClass: "text-zinc-800",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-dark py-20 text-brand-cream sm:py-24 lg:py-28">
      <div className="absolute inset-0 bg-gradient-radial-gold opacity-40" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
            Get Quick Answers
          </span>
          <h2 className="mt-5 text-3xl font-extrabold text-white sm:text-4xl">
            Everything you want to know — <span className="text-gradient-gold">upfront</span>
          </h2>
          <p className="mt-4 text-base text-brand-cream/75">
            No sales runaround. Jump straight to what matters most to you.
          </p>
        </div>

        <div className="mt-14 grid gap-8 sm:gap-6 md:grid-cols-3">
          {cards.map((c) => (
            <a
              key={c.title}
              href={c.href}
              className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-2 shadow-card ring-1 ring-brand-gold/20 backdrop-blur-sm transition-spring hover:-translate-y-1.5 hover:border-brand-gold/60 hover:shadow-card-hover"
            >
              <div className="relative h-52 overflow-hidden rounded-2xl bg-muted">
                <img
                  src={c.img}
                  alt={c.alt}
                  width={768}
                  height={512}
                  loading="eager"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-black/70 via-brand-black/10 to-transparent" />
                <span className="absolute left-4 top-4 rounded-full bg-background/90 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-foreground shadow-sm backdrop-blur">
                  {c.eyebrow}
                </span>
              </div>
              <div className="flex flex-1 flex-col px-5 pb-5 pt-6">
                <div className={`h-1 w-10 rounded-full bg-gradient-to-r ${c.accent}`} aria-hidden="true" />
                <h3 className="mt-4 text-2xl font-extrabold text-white">{c.title}</h3>
                <p className="mt-3 flex-1 leading-relaxed text-brand-cream/70">{c.desc}</p>
                <span className="mt-6 inline-flex items-center justify-between rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-bold text-brand-cream transition-smooth group-hover:border-brand-gold group-hover:bg-gradient-gold group-hover:text-brand-black">
                  {c.cta}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>

      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="max-w-4xl border-white/10 bg-brand-black p-0 sm:p-0">
          <DialogTitle className="sr-only">
            Meet Chris Heidlebaugh — About Colorado Web Impressions
          </DialogTitle>
          <div className="relative aspect-video w-full overflow-hidden rounded-lg">
            {videoOpen && (
              <iframe
                src="https://www.youtube-nocookie.com/embed/t3no84uGrms?rel=0&modestbranding=1&autoplay=1"
                title="About Colorado Web Impressions — Meet Chris Heidlebaugh"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 h-full w-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/* --------------------------- SECTION TITLE --------------------------- */
function SectionHeader({
  eyebrow,
  title,
  description,
  light,
}: {
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  light?: boolean;
}) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <span
        className={`inline-block rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] ${
          light
            ? "border-brand-gold/40 bg-brand-gold/10 text-brand-gold"
            : "border-brand-gold-deep/30 bg-brand-gold/10 text-brand-gold-deep"
        }`}
      >
        {eyebrow}
      </span>
      <h2
        className={`mt-5 text-4xl font-extrabold leading-[1.1] sm:text-5xl ${
          light ? "text-white" : "text-foreground"
        }`}
      >
        {title}
      </h2>
      {description && (
        <p
          className={`mx-auto mt-5 max-w-2xl text-lg leading-relaxed ${
            light ? "text-brand-cream/85" : "text-muted-foreground"
          }`}
        >
          {description}
        </p>
      )}
    </div>
  );
}

/* ----------------------------- SERVICES ----------------------------- */
const services = [
  {
    icon: Globe,
    title: "Web Design",
    to: "/colorado-springs-web-design",
    desc: "Fast, beautiful websites built to convert visitors into paying customers.",
    bullets: ["Conversion-focused design", "Mobile-first builds", "Lightning-fast speed"],
  },
  {
    icon: Search,
    title: "SEO",
    to: "/colorado-springs-seo",
    desc: "Rank on page one of Google for the searches your customers actually make.",
    bullets: ["Local & statewide SEO", "Technical audits", "Content that ranks"],
  },
  {
    icon: MapPin,
    title: "Google Maps Optimization",
    to: "/colorado-springs-google-maps",
    desc: "Show up in the Map Pack so nearby Colorado customers find you first.",
    bullets: ["GBP optimization", "Review strategy", "Map Pack ranking"],
  },
  {
    icon: Target,
    title: "PPC Advertising",
    to: "/colorado-springs-ppc",
    desc: "Profitable Google Ads campaigns laser-focused on lead generation.",
    bullets: ["Google Ads management", "Landing page builds", "Conversion tracking"],
  },
  {
    icon: Megaphone,
    title: "Digital Marketing",
    to: "/digital-marketing",
    desc: "Full-funnel digital marketing — social, email, content — that builds brand and books work.",
    bullets: ["Paid social ads", "Content & posting", "Lead-gen funnels"],
  },
  {
    icon: Bot,
    title: "GEO & AI Search Optimization",
    to: "/geo-ai",
    desc: "Get found in ChatGPT, Google AI Overviews, Perplexity, and Gemini — the new way customers search.",
    bullets: ["Generative Engine Optimization", "AI citation building", "Schema & entity SEO"],
  },
] as const;

export function Services() {
  return (
    <section id="services" className="scroll-mt-24 bg-section-alt py-24 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeader
          eyebrow="What We Do"
          title={
            <>
              Marketing services built for{" "}
              <span className="text-gradient-gold">Colorado businesses</span>
            </>
          }
          description="Every service is engineered to do one thing — get the phone ringing with qualified, high-intent leads in your Colorado service area."
        />

        <div className="mt-16 grid gap-8 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => (
            <Link
              key={s.title}
              to={s.to}
              className="group relative flex flex-col rounded-2xl border border-border bg-card p-7 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
            >
              <div className="mb-5 inline-grid h-12 w-12 place-items-center rounded-xl bg-secondary text-brand-gold transition-smooth group-hover:bg-gradient-gold group-hover:text-brand-black">
                <s.icon className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-foreground">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              <ul className="mt-4 space-y-2 text-sm text-foreground/80">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep" />
                    {b}
                  </li>
                ))}
              </ul>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold-deep">
                Learn more
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- RESULTS ----------------------------- */
export function Results() {
  const stats = [
    { value: "10x", label: "Average lead increase in 6 months" },
    { value: "200+", label: "Colorado websites built & ranked" },
    { value: "$8M+", label: "Revenue generated for our clients" },
    { value: "97%", label: "Client retention year over year" },
  ];
  return (
    <section className="relative overflow-hidden bg-gradient-dark py-24 text-brand-cream sm:py-28 lg:py-32">
      <div className="absolute inset-0 bg-gradient-radial-gold opacity-50" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeader
          light
          eyebrow="Real Outcomes"
          title={
            <>
              Turn your website into a{" "}
              <span className="text-gradient-gold">lead machine</span>
            </>
          }
          description="More phone calls. More booked jobs. More revenue. We don't sell vanity metrics — we sell outcomes that actually grow your Colorado business."
        />

        <div className="mt-16 grid gap-8 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-white/15 bg-white/[0.08] p-8 text-center backdrop-blur transition-smooth hover:border-brand-gold/60 hover:bg-white/[0.12]"
            >
              <div className="text-5xl font-black text-gradient-gold lg:text-6xl">{s.value}</div>
              <p className="mt-3 text-base font-medium leading-relaxed text-white/95">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 grid gap-8 sm:gap-6 md:grid-cols-3">
          {[
            { icon: PhoneCall, title: "More Phone Calls", desc: "Inbound calls from ready-to-buy customers in your area." },
            { icon: Calendar, title: "More Booked Jobs", desc: "Calendars filled with high-ticket, high-intent appointments." },
            { icon: TrendingUp, title: "More Revenue", desc: "Predictable monthly growth you can actually plan around." },
          ].map((b) => (
            <div
              key={b.title}
              className="flex items-start gap-4 rounded-xl border border-white/15 bg-white/[0.07] p-6"
            >
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-gradient-gold text-brand-black">
                <b.icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">{b.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-white/90">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- MAPS PROOF --------------------------- */
export function MapsProof() {
  const highlights = [
    { value: "1,400+", label: "5-star Google reviews generated for clients", icon: Star },
    { value: "Top 3", label: "Average Maps pack ranking within 90 days", icon: MapPin },
    { value: "+312%", label: "Avg. increase in profile views & calls", icon: TrendingUp },
    { value: "24/7", label: "Review monitoring & response service", icon: ShieldCheck },
  ];

  return (
    <section id="maps-proof" className="scroll-mt-24 bg-background py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeader
          eyebrow="Google Maps Optimization"
          title={
            <>
              We get Colorado businesses ranked in the{" "}
              <span className="text-gradient-gold">Maps 3-pack</span>
            </>
          }
          description="80% of local customers click the top 3 results in Google Maps. We optimize your Google Business Profile, build review velocity, and dominate your service area — so the next call goes to you."
        />

        <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
          <div className="relative">
            <div className="absolute -inset-6 rounded-3xl bg-gradient-radial-gold opacity-40 blur-2xl" />
            <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-card-hover">
              <img
                src={mapsProof1}
                alt="Google Maps screenshot showing a Colorado HVAC company ranking #1 in the local 3-pack with 4.9 stars and 187 reviews"
                width={768}
                height={1376}
                loading="lazy"
                className="h-auto w-full object-cover"
              />
            </div>
            <span className="absolute -right-3 -top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-gradient-gold px-4 py-2 text-xs font-bold uppercase tracking-wider text-brand-black shadow-gold">
              <Star className="h-3.5 w-3.5 fill-brand-black" />
              Ranked #1 in 90 days
            </span>
          </div>

          <div>
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-card">
              <img
                src={mapsProof2}
                alt="Google Business Profile dashboard showing 312% increase in profile views, 1,240 calls, and 890 direction requests"
                width={1200}
                height={896}
                loading="lazy"
                className="h-auto w-full object-cover"
              />
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {highlights.map((h) => (
                <div
                  key={h.label}
                  className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-card"
                >
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-secondary text-brand-gold-deep">
                    <h.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xl font-black text-foreground">{h.value}</div>
                    <p className="text-xs leading-snug text-muted-foreground">{h.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/colorado-springs-google-maps"
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                See how we rank in Maps
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-7 py-4 text-base font-semibold text-foreground transition-smooth hover:border-brand-gold/50 hover:bg-muted"
              >
                Get a Free Maps Audit
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- PROCESS ----------------------------- */
export function Process() {
  const steps = [
    {
      n: "01",
      title: "Strategy & Audit",
      desc: "We audit your website, rankings, competitors, and Colorado market — then build a clear plan to win.",
    },
    {
      n: "02",
      title: "Build & Optimize",
      desc: "We design, build, and optimize every asset — your site, GBP, ads, and content — to convert traffic into leads.",
    },
    {
      n: "03",
      title: "Generate Leads & Scale",
      desc: "We drive traffic, track every call, double down on what works, and scale your monthly leads.",
    },
  ];
  return (
    <section className="bg-section-alt py-24 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeader
          eyebrow="How We Help"
          title={
            <>
              A proven 3-step process to grow{" "}
              <span className="text-gradient-gold">your Colorado business</span>
            </>
          }
          description="No fluff. No long contracts. Just a clear, transparent system that turns marketing spend into measurable revenue."
        />

        <div className="mt-16 grid gap-8 sm:gap-6 md:grid-cols-3">
          {steps.map((s) => (
            <div
              key={s.n}
              className="relative rounded-2xl border border-border bg-card p-8 shadow-card transition-spring hover:-translate-y-1 hover:shadow-card-hover"
            >
              <div className="text-6xl font-black text-gradient-gold">{s.n}</div>
              <h3 className="mt-3 text-xl font-bold text-foreground">{s.title}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------- COLORADO LOCAL ------------------------- */
export function ColoradoLocal() {
  const cities = [
    "Colorado Springs",
    "Denver",
    "Castle Rock",
    "Pueblo",
    "Fort Collins",
    "Monument",
    "Aurora",
    "Parker",
    "Boulder",
    "Loveland",
  ];
  return (
    <section className="bg-background py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              🏔️ Colorado Local
            </span>
            <h2 className="mt-5 text-4xl font-extrabold leading-[1.1] text-foreground sm:text-5xl">
              We only work with{" "}
              <span className="text-gradient-gold">Colorado businesses</span>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              We know the Colorado market — Front Range competition, mountain-town seasonality,
              what local customers search for, and how to rank in the cities that actually matter
              for your business. No nationwide cookie-cutter playbooks. Just real strategy built
              for Colorado.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              {cities.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3.5 py-1.5 text-sm font-medium text-foreground transition-smooth hover:border-brand-gold hover:bg-brand-gold/10"
                >
                  <MapPin className="h-3.5 w-3.5 text-brand-gold-deep" />
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-gold opacity-20 blur-2xl" />
            <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-elegant">
              <img
                src={heroImg}
                alt="Colorado Springs digital marketing agency serving the Front Range"
                width={1920}
                height={1280}
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-black/90 to-transparent p-6">
                <div className="flex items-center gap-3 text-white">
                  <MapPin className="h-5 w-5 text-brand-gold" />
                  <div>
                    <div className="text-sm font-semibold uppercase tracking-wider text-brand-gold">
                      Headquarters
                    </div>
                    <div className="text-lg font-bold">Colorado Springs, CO</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- WHY US ----------------------------- */
export function WhyUs() {
  const points = [
    {
      title: "Colorado-Focused Strategy",
      desc: "We understand the Front Range market, mountain-town seasonality, and how Coloradans actually search.",
    },
    {
      title: "No Fluff. Real Results.",
      desc: "We don't sell impressions or vanity metrics. We sell phone calls, leads, and revenue.",
    },
    {
      title: "Transparent Communication",
      desc: "Honest answers, real humans, and answers to your questions in hours — not weeks.",
    },
    {
      title: "Built for Lead Generation",
      desc: "Every site, page, and ad is engineered with one goal: more qualified leads in your inbox.",
    },
    {
      title: "No Long Contracts",
      desc: "We earn your business every month. If we're not delivering, you're not stuck.",
    },
    {
      title: "Owner-Led, Not Outsourced",
      desc: "You work directly with the team building your campaigns — no junior account managers.",
    },
  ];
  return (
    <section className="bg-background py-24 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeader
          eyebrow="Why Choose Us"
          title={
            <>
              Why Colorado business owners{" "}
              <span className="text-gradient-gold">trust us</span>
            </>
          }
          description="Owner-led. In-house. Built for Colorado businesses that take growth seriously — and want to talk to the person actually doing the work."
        />

        <div className="mt-16 grid gap-8 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
          {points.map((p) => (
            <div
              key={p.title}
              className="flex gap-4 rounded-2xl border border-border bg-card p-6 transition-smooth hover:border-brand-gold/50 hover:shadow-card-hover"
            >
              <CheckCircle2 className="h-6 w-6 shrink-0 text-brand-gold-deep" />
              <div>
                <h3 className="text-lg font-bold text-foreground">{p.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ----------------------------- PORTFOLIO ----------------------------- */
export function Portfolio() {
  const projects = [
    { img: portfolio1, title: "HVAC Company Website", category: "HVAC · Colorado Springs", outcome: "+340% qualified leads" },
    { img: portfolio2, title: "Plumbing Company Website", category: "Plumbing · Front Range", outcome: "Page 1 in 90 days" },
    { img: portfolio3, title: "Roofing Company Website", category: "Roofing · Denver Metro", outcome: "12x return on ad spend" },
  ];
  return (
    <section id="portfolio" className="bg-background py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeader
          eyebrow="Recent Work"
          title={
            <>
              Websites & campaigns built to{" "}
              <span className="text-gradient-gold">convert</span>
            </>
          }
          description="A snapshot of Colorado service businesses we've helped scale with high-converting websites and lead-driven marketing."
        />

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <div
              key={p.title}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-spring hover:-translate-y-1 hover:shadow-card-hover"
            >
              <div className="aspect-[4/3] overflow-hidden bg-muted">
                <img
                  src={p.img}
                  alt={`${p.title} website portfolio screenshot`}
                  width={1024}
                  height={768}
                  loading="lazy"
                  className="h-full w-full object-cover transition-spring group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-brand-gold-deep">
                  {p.category}
                </p>
                <h3 className="mt-1.5 text-lg font-bold text-foreground">{p.title}</h3>
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
                  <TrendingUp className="h-3.5 w-3.5 text-brand-gold" />
                  {p.outcome}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- TESTIMONIALS ----------------------------- */
export function Testimonials() {
  const reviews = [
    {
      quote:
        "Our phone hasn't stopped ringing since they rebuilt our site and dialed in our SEO. Best marketing investment we've ever made.",
      role: "HVAC Company Owner",
      city: "Colorado Springs",
    },
    {
      quote:
        "We went from page 4 to the top of the Map Pack in under 5 months. They truly understand the Colorado market.",
      role: "Plumbing Company Owner",
      city: "Monument, CO",
    },
    {
      quote:
        "Other agencies talked. They actually delivered. 12x return on our Google Ads in the first quarter.",
      role: "Roofing Company Founder",
      city: "Denver",
    },
    {
      quote:
        "Transparent, fast, and they actually pick up the phone. Our website is finally a real sales asset.",
      role: "Landscaping Company Owner",
      city: "Castle Rock",
    },
    {
      quote:
        "Booked solid through the busy season. We've had to hire two more techs to keep up with the leads.",
      role: "Electrical Contractor",
      city: "Pueblo",
    },
    {
      quote:
        "They get small business. They get Colorado. And they get results. Couldn't recommend them more.",
      role: "Cleaning Company Owner",
      city: "Fort Collins",
    },
  ];
  return (
    <section className="bg-background py-24 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeader
          eyebrow="Client Wins"
          title={
            <>
              What Colorado business owners are{" "}
              <span className="text-gradient-gold">saying</span>
            </>
          }
        />

        <div className="mt-16 grid gap-8 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r) => (
            <figure
              key={r.role + r.city}
              className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 shadow-card transition-smooth hover:border-brand-gold/50 hover:shadow-card-hover"
            >
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-brand-gold text-brand-gold" />
                ))}
              </div>
              <blockquote className="mt-4 flex-1 text-base leading-relaxed text-foreground/90">
                "{r.quote}"
              </blockquote>
              <figcaption className="mt-6 border-t border-border pt-4">
                <div className="font-bold text-foreground">{r.role}</div>
                <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-brand-gold-deep">
                  <MapPin className="h-3 w-3" /> {r.city}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- CTA ----------------------------- */
export function FinalCTA() {
  return (
    <section className="relative overflow-hidden bg-gradient-dark py-24 text-brand-cream sm:py-28 lg:py-32">
      <div className="absolute inset-0 bg-gradient-radial-gold opacity-60" />
      <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
        <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
          <MessageSquare className="h-3.5 w-3.5" /> Free Strategy Session
        </span>
        <h2 className="mt-6 text-4xl font-black leading-[1.05] text-white sm:text-5xl lg:text-6xl">
          Ready to get more <span className="text-gradient-gold">leads & sales?</span>
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-brand-cream/80">
          Book a free 30-minute strategy call. We'll audit your current marketing, show you exactly
          where the leaks are, and outline a clear plan to grow your Colorado business.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-8 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a Free Proposal
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" />
            (719) 419-3935
          </a>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- PAIN / PROMISE ----------------------------- */
export function PainPromise() {
  const pains = [
    {
      pain: "Cold-called by an out-of-state agency that doesn't know the first thing about your business or Colorado.",
      promise: "We're based in Colorado Springs, family-owned for over 25 years, and we only work with Colorado businesses.",
    },
    {
      pain: "AI bots spamming your inbox telling you your website \"sucks\" — full of lies just to book a sales call.",
      promise: "No bots, no scare tactics. You get a real audit from Chris, the owner, with honest answers and no pressure.",
    },
    {
      pain: "A slick salesperson sold you the dream, then handed you off and disappeared.",
      promise: "You work directly with Chris, the owner. The same person who builds your strategy runs it.",
    },
    {
      pain: "Hired a \"marketing company\" or TV station that white-labels and outsources every bit of the work.",
      promise: "We do 100% of the work in-house. No subcontractors, no overseas teams, no hand-offs.",
    },
    {
      pain: "Passed around between account managers, never sure who's actually doing the work.",
      promise: "One owner. One small in-house team that knows you, your business, and your customers by name.",
    },
    {
      pain: "Paying for things you don't understand — or being charged way too much for very little.",
      promise: "Fair, transparent pricing. No hidden fees, no surprises, ever.",
    },
    {
      pain: "Locked into a 12-month contract with an agency that stopped caring after month two.",
      promise: "Month-to-month. We earn your business every single month — or you walk, no penalty.",
    },
    {
      pain: "Lots of \"impressions\" and \"reach\" — but the phone still isn't ringing.",
      promise: "We measure what actually matters: calls, leads, booked jobs, and real revenue.",
    },
  ];
  return (
    <section className="bg-background py-24 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeader
          eyebrow="Sound Familiar?"
          title={
            <>
              Tired of being sold by people who{" "}
              <span className="text-gradient-gold">never do the work?</span>
            </>
          }
          description="Most Colorado business owners we meet have been burned by an agency before. Here's what's different about working with us."
        />

        <div className="mx-auto mt-16 grid max-w-5xl gap-7 sm:gap-5">
          {pains.map((p) => (
            <div
              key={p.pain}
              className="grid gap-0 overflow-hidden rounded-2xl border border-border bg-card shadow-card md:grid-cols-2"
            >
              <div className="flex items-start gap-4 border-b border-border bg-muted/40 p-6 md:border-b-0 md:border-r">
                <XCircle className="mt-0.5 h-6 w-6 shrink-0 text-destructive/70" />
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    The Problem
                  </div>
                  <p className="mt-1.5 text-base font-medium text-foreground/90">{p.pain}</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-6">
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-gold-deep" />
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                    How We're Different
                  </div>
                  <p className="mt-1.5 text-base font-semibold text-foreground">{p.promise}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-12 max-w-2xl rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6 text-center">
          <p className="text-lg font-semibold text-foreground">
            A faith-based, family-owned Colorado agency that actually picks up the phone, does the work in-house, and cares about your business as much as you do.
          </p>
          <Link
            to="/contact"
            className="group mt-5 inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-3.5 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a Free Proposal
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- MEET THE OWNER ----------------------------- */
export function MeetTheOwner() {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <section className="bg-gradient-dark py-24 text-brand-cream sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-5">
          <div className="relative lg:col-span-2">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-gold opacity-25 blur-3xl" />
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-transparent shadow-elegant">
              <img
                src={ownerImg}
                alt="Chris Heidlebaugh — Owner of Colorado Web Impressions, a Colorado Springs web design, SEO, and digital marketing agency"
                width={576}
                height={800}
                loading="lazy"
                className="mx-auto block h-auto w-full max-w-md object-contain"
                data-geo-lat="38.9359976"
                data-geo-lng="-104.7653118"
                data-geo-city="Colorado Springs"
              />
            </div>
          </div>

          <div className="lg:col-span-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
              <Handshake className="h-3.5 w-3.5" />
              You Work Directly With The Owner
            </span>
            <h2 className="mt-5 text-4xl font-extrabold leading-[1.1] text-white sm:text-5xl">
              Hi, I'm Chris.{" "}
              <span className="text-gradient-gold">No salespeople. No middlemen.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-brand-cream/80">
              I started Colorado Web Impressions back in 2006 because I was tired of seeing local
              business owners get burned by big agencies that overpromised, overcharged, and
              disappeared. When you work with us, you work directly with me. I answer the phone. I
              build the strategy. I'm the one accountable for your results — and I take that
              personally.
            </p>
            <p className="mt-3 text-sm font-semibold uppercase tracking-wider text-brand-gold">
              — Chris Heidlebaugh, Owner
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[
                { icon: Phone, t: "Talk To The Owner Directly", d: "Call me direct — no gatekeepers." },
                { icon: Heart, t: "I actually care.", d: "Your wins are my wins. That's the deal." },
                { icon: ShieldCheck, t: "I'm accountable.", d: "One owner. One throat to choke." },
                { icon: MapPin, t: "I'm in Colorado.", d: "Local, in your time zone, every day." },
              ].map((b) => (
                <div
                  key={b.t}
                  className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4"
                >
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gradient-gold text-brand-black">
                    <b.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-bold text-white">{b.t}</div>
                    <div className="text-sm text-brand-cream/85">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-3.5 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                Get a Free Proposal
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+17194193935"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-3.5 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
               aria-label="Call (719) 419-3935">
                <Phone className="h-4 w-4" />
                Call the Owner Directly
              </a>
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                className="group inline-flex items-center justify-center gap-2 rounded-md border border-brand-gold/40 bg-brand-gold/10 px-7 py-3.5 text-base font-semibold text-brand-gold backdrop-blur transition-smooth hover:border-brand-gold hover:bg-brand-gold/20"
                aria-label="Watch the 2-minute intro video about Chris and Colorado Web Impressions"
              >
                <PlayCircle className="h-5 w-5 transition-spring group-hover:scale-110" />
                Watch · Meet Chris (2 min)
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="max-w-4xl border-white/10 bg-brand-black p-0 sm:p-0">
          <DialogTitle className="sr-only">
            Meet Chris Heidlebaugh — About Colorado Web Impressions
          </DialogTitle>
          <div className="relative aspect-video w-full overflow-hidden rounded-lg">
            {videoOpen && (
              <iframe
                src="https://www.youtube-nocookie.com/embed/t3no84uGrms?rel=0&modestbranding=1&autoplay=1"
                title="About Colorado Web Impressions — Meet Chris Heidlebaugh"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 h-full w-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/* ----------------------------- PRICING TRANSPARENCY ----------------------------- */
export function Pricing() {
  return <PricingTeaserStrip />;
}

/* ----------------------------- EDUCATION / VIDEOS ----------------------------- */
export function Education() {
  const lessons = [
    {
      title: "How Google Maps Ranking Actually Works",
      length: "6 min",
      cat: "Local SEO",
      desc: "The 3 signals that move you into the Map Pack — and the myths to ignore.",
    },
    {
      title: "What a $5K Website Should Actually Include",
      length: "8 min",
      cat: "Web Design",
      desc: "A no-BS breakdown of what you should expect — and red flags to watch for.",
    },
    {
      title: "Google Ads vs. SEO: Where to Spend First",
      length: "5 min",
      cat: "Strategy",
      desc: "Which channel makes sense for your stage of business — explained simply.",
    },
    {
      title: "Why Your Website Isn't Generating Leads",
      length: "7 min",
      cat: "Conversion",
      desc: "The 5 fixes that turn a brochure site into a 24/7 lead machine.",
    },
  ];
  return (
    <section className="bg-section-alt py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeader
          eyebrow="Learning Center"
          title={
            <>
              We teach you{" "}
              <span className="text-gradient-gold">exactly what we do</span>
            </>
          }
          description="Free videos that pull back the curtain on web design, SEO, Google Maps, and lead generation. Watch, learn, and decide for yourself if we're the real deal — no sign-up required."
        />

        <div className="mt-14 grid gap-8 sm:gap-6 md:grid-cols-2">
          {lessons.map((l) => (
            <article
              key={l.title}
              className="group flex gap-5 rounded-2xl border border-border bg-card p-5 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
            >
              <div className="relative grid aspect-video w-44 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-dark">
                <div className="absolute inset-0 bg-gradient-radial-gold opacity-40" />
                <PlayCircle className="relative h-12 w-12 text-brand-gold transition-spring group-hover:scale-110" />
                <span className="absolute bottom-2 right-2 rounded-md bg-brand-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white backdrop-blur">
                  {l.length}
                </span>
              </div>
              <div className="flex flex-1 flex-col">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                  {l.cat}
                </span>
                <h3 className="mt-1 text-lg font-bold leading-tight text-foreground">{l.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                  {l.desc}
                </p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-gold-deep">
                  Watch the video
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </article>
          ))}
        </div>

        <div className="mx-auto mt-12 flex max-w-2xl flex-col items-center gap-4 rounded-2xl border border-border bg-card p-6 text-center shadow-card sm:flex-row sm:text-left">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gradient-gold text-brand-black">
            <GraduationCap className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-foreground">New videos every month.</div>
            <div className="text-sm text-muted-foreground">
              Honest education — never a sales pitch.
            </div>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-secondary px-5 py-2.5 text-sm font-bold text-secondary-foreground transition-smooth hover:border-brand-gold"
          >
            Browse all lessons
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- INDUSTRIES & SERVICE AREAS ----------------------------- */
export function Industries() {
  const industries = [
    { icon: Wrench, name: "HVAC" },
    { icon: Truck, name: "Plumbing" },
    { icon: Zap, name: "Electrical" },
    { icon: Trees, name: "Landscaping" },
    { icon: HomeIcon, name: "Roofing" },
    { icon: Scale, name: "Law Firms" },
    { icon: Stethoscope, name: "Dental" },
    { icon: Utensils, name: "Food & Drink" },
    { icon: GlassWater, name: "Wedding Venues" },
    { icon: Car, name: "Automotive" },
  ];
  const regions = [
    {
      name: "Front Range",
      cities: ["Colorado Springs", "Denver", "Castle Rock", "Monument", "Parker", "Aurora"],
    },
    {
      name: "Northern Colorado",
      cities: ["Fort Collins", "Loveland", "Greeley", "Boulder", "Longmont", "Windsor"],
    },
    {
      name: "Southern Colorado",
      cities: ["Pueblo", "Cañon City", "Trinidad", "Salida", "Woodland Park", "Fountain"],
    },
    {
      name: "Western Slope & Mountains",
      cities: ["Grand Junction", "Glenwood Springs", "Durango", "Steamboat Springs", "Vail", "Frisco"],
    },
  ];
  return (
    <section id="industries" className="scroll-mt-24 bg-section-alt py-24 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeader
          eyebrow="Built For Colorado"
          title={
            <>
              The industries we serve — across{" "}
              <span className="text-gradient-gold">all of Colorado</span>
            </>
          }
          description="From the Front Range to the Western Slope, we help home-service businesses generate more calls, more booked jobs, and more revenue."
        />

        <div className="mt-14">
          <h3 className="text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
            Industries We Specialize In
          </h3>
          <div className="mx-auto mt-6 grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-5">
            {industries.map((i) => (
              <div
                key={i.name}
                className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-5 text-center shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
              >
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-brand-gold-deep">
                  <i.icon className="h-6 w-6" />
                </div>
                <div className="text-sm font-bold text-foreground">{i.name}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/industries"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-gold px-7 py-3.5 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              View all industries we serve
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div className="relative mx-auto mt-20 max-w-6xl overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-brand-black via-zinc-900 to-brand-black p-8 shadow-card sm:p-12 lg:p-14">
          {/* Decorative gold glows */}
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-gold/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-gold/10 blur-3xl" />
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-gold" />

          <div className="relative grid items-center gap-8 lg:grid-cols-[1fr_auto] lg:gap-12">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/30 bg-brand-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-gold">
                <MapPin className="h-3.5 w-3.5" />
                Serving All Of Colorado
              </div>
              <h3 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[2.5rem] lg:leading-tight">
                From the Front Range to the{" "}
                <span className="text-gradient-gold">Western Slope</span>
              </h3>
              <p className="mt-3 max-w-xl text-base text-white/90 lg:text-lg">
                Denver, Colorado Springs, Fort Collins, Pueblo, Grand Junction, Durango — and 60+
                more Colorado cities. Wherever your customers are, we help them find you.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  "Denver",
                  "Colorado Springs",
                  "Aurora",
                  "Fort Collins",
                  "Boulder",
                  "Lakewood",
                  "Pueblo",
                  "Castle Rock",
                  "Grand Junction",
                  "Durango",
                ].map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 backdrop-blur"
                  >
                    <MapPin className="h-3 w-3 text-brand-gold" />
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
              <Link
                to="/service-areas"
                className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-gradient-gold px-6 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                View All Service Areas
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-white/30 bg-white/5 px-6 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
              >
                Get a Free Quote
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
